import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useBoolean } from "@/hooks/use-boolean.ts";
import { useCurrentShopLocalesAvailables } from "@/lib/translator";
import useUpdateCustomMessage from "@babackapp/private-api-client/client/use-update-portal-custom-message";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { PropsWithChildren, useMemo } from "react";
import {
  CustomKey,
  CustomMessagesSettingsForm,
} from "./custom-message-settings-form.tsx";
import type {
  ShopPreferencesUserPortalDto,
  TextDto,
} from "@babackapp/private-api-client/types";

type UpdateCustomMessageDialogProps = {
  customKey: CustomKey;
  disabled?: boolean;
};

export function UpdateCustomMessageDialog({
  children,
  customKey,
  disabled,
}: PropsWithChildren<UpdateCustomMessageDialogProps>) {
  const t = useTranslation();
  const open = useBoolean(false);
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateCustomMessage } = useUpdateCustomMessage(shop);
  const languages = useCurrentShopLocalesAvailables();

  const values = useMemo(() => {
    const source = (
      shop.preferences.userPortal[
        customKey as keyof ShopPreferencesUserPortalDto
      ] ?? []
    ) as TextDto[];
    const available = source.reduce((acc, curr) => {
      acc[curr.lang] = curr.text;
      return acc;
    }, {} as Record<string, string>);

    return languages.map((lang) => {
      return {
        lang: lang.code,
        text: available[lang.code] ?? "",
      };
    });
  }, [shop, customKey, languages]);

  return (
    <Dialog open={open.value} onOpenChange={open.setValue}>
      <DialogTrigger disabled={disabled}>{children}</DialogTrigger>
      <DialogContent className="pt-8 max-w-[700px] w-[700px]">
        <DialogHeader className="flex flex-col justify-start items-start">
          <DialogTitle>{t("settings.customMessage.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.customMessage.title")}
          </DialogDescription>
        </DialogHeader>
        <CustomMessagesSettingsForm
          customKey={customKey}
          defaultValues={{ values }}
          onSubmit={async (data) => {
            return updateCustomMessage({
              key: customKey,
              value: data,
            });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
